import { css } from '@emotion/react';
import { omit, pick } from 'ramda';
import * as React from 'react';

import colors from '../../theme/colors';
import { spacingKeys, spacings } from '../../theme/variables';
import Button from './Button';
import { ButtonProps } from './Button.types';

interface IIconButton extends Omit<ButtonProps, 'children'> {
  icon: React.ComponentType<{ color?: string }>;
  focus?: boolean;
  color?: string;
  badge?: boolean;
  badgeColor?: string;
}

const getIconColor = ({ variant, disabled, color }: { variant?: string; disabled: boolean; color: string }) => {
  if (disabled) {
    return colors.neutral300;
  }
  if (variant) {
    return undefined;
  }
  return color;
};

const IconButton = ({
  onClick,
  focus,
  customCss,
  icon: Icon,
  color = colors.blue400,
  disabled = false,
  badge,
  badgeColor = colors.cherise50,
  variant,
  ...props
}: IIconButton) => {
  const spacingProps = pick(spacingKeys, props);
  const restOfProps = omit(spacingKeys, props);

  return (
    <Button
      borderless={!variant}
      variant={variant}
      p={spacings.px4}
      disabled={disabled}
      customCss={css`
        border-radius: 8px;
        position: relative;
        padding: 8px;
        ${
          !variant &&
          css`
            background-color: ${focus ? colors.neutral100 : 'transparent'};
            &:hover {
              background-color: ${colors.neutral100};
            }
          `
        }
        line-height: 1;
        ${customCss}
      `}
      onClick={onClick}
      {...spacingProps}
      {...restOfProps}
    >
      <Icon color={getIconColor({ variant, disabled, color })} />
      {badge && (
        <div
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
            width: 8px;
            height: 8px;
            border-radius: 4px;
            background-color: ${badgeColor};
          `}
        />
      )}
    </Button>
  );
};

export default IconButton;
